import type { ExerciseDef } from './types'

// ─── 2.1 The number line ──────────────────────────────────────────────────────

export const negatives: ExerciseDef = {
  starter: `console.log(-3 + 5)      // start at −3, move 5 right
console.log(2 - 7)       // start at 2, move 7 left
console.log(-2 * 3)      // a negative times a positive: flips to the other side
console.log(-2 * -3)     // two flips land you back on the positive side
console.log(Math.abs(-7), Math.abs(7))   // distance from zero
`,
}

export const errors: ExerciseDef = {
  starter: `// A model predicted these prices. Here's what actually happened:
const predicted = [210, 150, 300]
const actual    = [200, 165, 300]

// 1. errors: actual minus predicted, for each one → [-10, 15, 0]
function errors(p: number[], a: number[]): number[] {
  return []
}

// 2. squaredErrors: each error squared → [100, 225, 0]
function squaredErrors(p: number[], a: number[]): number[] {
  return []
}

console.log(errors(predicted, actual), squaredErrors(predicted, actual))
`,
  solution: `const predicted = [210, 150, 300]
const actual    = [200, 165, 300]

function errors(p: number[], a: number[]): number[] {
  return p.map((pi, i) => a[i] - pi)
}

function squaredErrors(p: number[], a: number[]): number[] {
  return errors(p, a).map(e => e * e)
}

console.log(errors(predicted, actual), squaredErrors(predicted, actual))
`,
  tests: `
test("errors are actual − predicted", () => expect(errors([210, 150, 300], [200, 165, 300]), "errors(...)").toEqual([-10, 15, 0]))
test("squared errors are never negative", () => expect(squaredErrors([210, 150, 300], [200, 165, 300]), "squaredErrors(...)").toEqual([100, 225, 0]))
test("works for another example", () => expect(squaredErrors([1, 2], [4, 0]), "squaredErrors([1, 2], [4, 0])").toEqual([9, 4]))
`,
}

// ─── 2.2 Graphs ───────────────────────────────────────────────────────────────

export const plotRule: ExerciseDef = {
  starter: `// A rule, drawn. Change it and run again!
const f = (x: number) => x * x - 4

plot.fn(f, [-4, 4], { title: "f(x) = x² − 4" })

// A table of values, like you'd do by hand:
for (const x of [-3, -2, -1, 0, 1, 2, 3]) {
  console.log("x =", x, "  f(x) =", f(x))
}
`,
}

export const zeros: ExerciseDef = {
  starter: `// f(x) = x² − 4 crosses the x-axis (where f(x) = 0) twice.

// 1. Write f.
function f(x: number): number {
  return 0
}

// 2. Find every whole number x from −10 to 10 where f(x) is exactly 0.
function wholeZeros(): number[] {
  const found: number[] = []
  // loop over x = −10, −9, ..., 10 and push the ones where f(x) === 0
  return found
}

console.log(wholeZeros())
`,
  solution: `function f(x: number): number {
  return x * x - 4
}

function wholeZeros(): number[] {
  const found: number[] = []
  for (let x = -10; x <= 10; x++) {
    if (f(x) === 0) found.push(x)
  }
  return found
}

console.log(wholeZeros())
`,
  tests: `
test("f(0) is −4", () => expect(f(0), "f(0)").toBe(-4))
test("f(3) is 5", () => expect(f(3), "f(3)").toBe(5))
test("the zeros are −2 and 2", () => expect(wholeZeros(), "wholeZeros()").toEqual([-2, 2]))
`,
}

// ─── 2.3 Lines ────────────────────────────────────────────────────────────────

export const lineThrough: ExerciseDef = {
  starter: `// slope through two points: rise ÷ run
function slope(x1: number, y1: number, x2: number, y2: number): number {
  return 0
}

// The line y = m·x + b through both points. Find m with slope(),
// then b: since y1 = m·x1 + b, we get b = y1 − m·x1.
function lineThrough(x1: number, y1: number, x2: number, y2: number): { m: number; b: number } {
  return { m: 0, b: 0 }
}

console.log(slope(1, 3, 3, 7), lineThrough(1, 3, 3, 7))
`,
  solution: `function slope(x1: number, y1: number, x2: number, y2: number): number {
  return (y2 - y1) / (x2 - x1)
}

function lineThrough(x1: number, y1: number, x2: number, y2: number): { m: number; b: number } {
  const m = slope(x1, y1, x2, y2)
  return { m, b: y1 - m * x1 }
}

console.log(slope(1, 3, 3, 7), lineThrough(1, 3, 3, 7))
`,
  tests: `
test("slope from (1,3) to (3,7) is 2", () => expect(slope(1, 3, 3, 7), "slope(1, 3, 3, 7)").toBeCloseTo(2))
test("slope can be negative", () => expect(slope(0, 4, 2, 0), "slope(0, 4, 2, 0)").toBeCloseTo(-2))
test("lineThrough(1,3,3,7) is m=2, b=1", () => expect(lineThrough(1, 3, 3, 7), "lineThrough(1, 3, 3, 7)").toBeCloseTo({ m: 2, b: 1 }))
test("lineThrough(−2,0,2,2) is m=0.5, b=1", () => expect(lineThrough(-2, 0, 2, 2), "lineThrough(-2, 0, 2, 2)").toBeCloseTo({ m: 0.5, b: 1 }))
`,
}

export const predictLine: ExerciseDef = {
  starter: `// The simplest machine-learning model: predict y from x with a line.
// The two numbers m and b are its "parameters".
const m = 1.8
const b = 32

const predict = (x: number) => m * x + b

// This one converts °C to °F. Try changing m and b and see the line move.
plot.fn(predict, [-10, 40], { title: "y = 1.8x + 32" })
console.log(predict(25))
`,
}

// ─── 2.4 Exponents & logs ─────────────────────────────────────────────────────

export const powers: ExerciseDef = {
  starter: `console.log(2 ** 3)          // 2 × 2 × 2
console.log(2 ** 0)          // anything to the power 0 is 1
console.log(2 ** -1)         // negative power: 1 ÷ 2
console.log(Math.E)          // e ≈ 2.718
console.log(Math.exp(1))     // e¹ — "exp" means "e to the power"
console.log(Math.log(Math.E))  // log undoes exp: back to 1
console.log(Math.log2(1024))   // how many doublings make 1024?

plot.fn([x => 2 ** x, x => Math.exp(x)], [-3, 3], { title: "2ˣ and eˣ" })
`,
}

export const logs: ExerciseDef = {
  starter: `// 1. doublings(n): how many times must you double 1 to reach at least n?
//    doublings(8) → 3   (1→2→4→8),   doublings(9) → 4   (…→8→16)
//    Use a loop — no Math.log allowed for this one!
function doublings(n: number): number {
  return 0
}

// 2. logBase(x, base): the logarithm of x in any base.
//    Use the rule: log_base(x) = Math.log(x) / Math.log(base)
function logBase(x: number, base: number): number {
  return 0
}

console.log(doublings(1000), logBase(1000, 10))
`,
  solution: `function doublings(n: number): number {
  let value = 1
  let count = 0
  while (value < n) {
    value = value * 2
    count++
  }
  return count
}

function logBase(x: number, base: number): number {
  return Math.log(x) / Math.log(base)
}

console.log(doublings(1000), logBase(1000, 10))
`,
  tests: `
test("doublings(8) is 3", () => expect(doublings(8), "doublings(8)").toBe(3))
test("doublings(9) is 4", () => expect(doublings(9), "doublings(9)").toBe(4))
test("doublings(1) is 0", () => expect(doublings(1), "doublings(1)").toBe(0))
test("logBase(1000, 10) is 3", () => expect(logBase(1000, 10), "logBase(1000, 10)").toBeCloseTo(3))
test("logBase(81, 3) is 4", () => expect(logBase(81, 3), "logBase(81, 3)").toBeCloseTo(4))
`,
}

// ─── 2.5 Sums & averages ──────────────────────────────────────────────────────

export const sigmaLoop: ExerciseDef = {
  starter: `// Σ from i = 1 to 4 of i²  is just a loop:
let total = 0
for (let i = 1; i <= 4; i++) {
  total += i * i
}
console.log(total)   // 1 + 4 + 9 + 16
`,
}

export const sigmaMean: ExerciseDef = {
  starter: `// 1. sigma(n, f): the sum of f(i) for i = 1, 2, ..., n.
//    sigma(4, i => i) → 10
function sigma(n: number, f: (i: number) => number): number {
  return 0
}

// 2. mean(xs): the average — sum of the values divided by how many there are.
function mean(xs: number[]): number {
  return 0
}

console.log(sigma(4, i => i), mean([2, 4, 9]))
`,
  solution: `function sigma(n: number, f: (i: number) => number): number {
  let total = 0
  for (let i = 1; i <= n; i++) total += f(i)
  return total
}

function mean(xs: number[]): number {
  let total = 0
  for (const x of xs) total += x
  return total / xs.length
}

console.log(sigma(4, i => i), mean([2, 4, 9]))
`,
  tests: `
test("sigma(4, i => i) is 10", () => expect(sigma(4, i => i), "sigma(4, i => i)").toBe(10))
test("sigma(3, i => i * i) is 14", () => expect(sigma(3, i => i * i), "sigma(3, i => i * i)").toBe(14))
test("sigma(100, i => i) is 5050", () => expect(sigma(100, i => i), "sigma(100, i => i)").toBe(5050))
test("mean([2, 4, 9]) is 5", () => expect(mean([2, 4, 9]), "mean([2, 4, 9])").toBeCloseTo(5))
test("mean of one number is itself", () => expect(mean([7]), "mean([7])").toBeCloseTo(7))
`,
}

export const mse: ExerciseDef = {
  starter: `// Combine everything from this module:
//   meanSquaredError = (1/n) Σ (actual_i − predicted_i)²
// It's the most famous formula in machine learning, and you can already read it.
function meanSquaredError(predicted: number[], actual: number[]): number {
  return 0
}

console.log(meanSquaredError([210, 150, 300], [200, 165, 300]))
`,
  solution: `function meanSquaredError(predicted: number[], actual: number[]): number {
  let total = 0
  for (let i = 0; i < actual.length; i++) {
    const e = actual[i] - predicted[i]
    total += e * e
  }
  return total / actual.length
}

console.log(meanSquaredError([210, 150, 300], [200, 165, 300]))
`,
  tests: `
test("MSE of the price example is 108.33…", () => expect(meanSquaredError([210, 150, 300], [200, 165, 300]), "meanSquaredError(...)").toBeCloseTo(325 / 3))
test("a perfect prediction has MSE 0", () => expect(meanSquaredError([1, 2, 3], [1, 2, 3]), "meanSquaredError(same, same)").toBe(0))
test("errors in either direction count the same", () => expect(meanSquaredError([0, 0], [2, -2]), "meanSquaredError([0, 0], [2, -2])").toBeCloseTo(4))
`,
}
